/* ═════════════════════════════════════════════════════════════════
   Public Audit endpoint — Razorpay-shareable, password-protected
   ═════════════════════════════════════════════════════════════════ */
import { useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Lock, Loader2, ShieldCheck, BadgeCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PulseAudit() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [password, setPassword] = useState(params.get("p") || "");
  const [busy, setBusy] = useState(false);
  const [audit, setAudit] = useState(null);

  const unlock = async () => {
    if (!password) { toast.error("Password required"); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/public/audit/${token}/unlock`, { password });
      setAudit(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Incorrect password");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#070505] text-[#F5F5F0]">
      <Toaster theme="dark" position="top-center" richColors />
      <div className="max-w-3xl mx-auto px-6 py-14">
        <div className="flex items-center gap-3 mb-6">
          <ShieldCheck className="text-[#D8B23E]" size={28} />
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#D8B23E]">Vinci Compliance Dossier</div>
            <h1 className="font-serif text-2xl mt-1 text-[#F6E4A6]">Password-Protected Audit Link</h1>
          </div>
        </div>

        {!audit ? (
          <div className="rounded-sm border border-[#D8B23E]/40 bg-[#0A0906] p-6 max-w-md">
            <div className="text-[10px] tracking-[0.22em] uppercase text-[#8A6A1A] flex items-center gap-2">
              <Lock size={11} /> Unlock Dossier
            </div>
            <input data-testid="audit-password"
                     value={password} onChange={(e) => setPassword(e.target.value.toUpperCase())}
                     placeholder="12-character audit password"
                     className="mt-3 w-full bg-black/50 border border-white/20 rounded-sm px-3 py-2 text-[#F5F5F0] font-mono" />
            <button data-testid="audit-unlock"
                      onClick={unlock} disabled={busy}
                      className="mt-3 w-full text-[11px] font-grotesk uppercase tracking-[0.22em] text-black bg-gradient-to-r from-[#D8B23E] via-[#F6E4A6] to-[#B87333] px-6 py-2.5 rounded-sm inline-flex items-center justify-center gap-1.5">
              {busy ? <Loader2 size={12} className="animate-spin" /> : <BadgeCheck size={12} />}
              Unlock
            </button>
          </div>
        ) : (
          <div className="space-y-5">
            <div className="rounded-sm border border-[#D8B23E]/40 p-5 bg-[#0A0906]">
              <div className="text-[10px] tracking-[0.22em] uppercase text-[#D8B23E]">Partner</div>
              <div className="font-serif text-xl mt-1">{audit.partner.name}</div>
              <div className="text-[11px] text-[#8A8A82] font-mono">
                {audit.partner.email} · {audit.partner.vinci_code || "—"}
              </div>
            </div>
            {audit.form && (
              <div className="rounded-sm border border-white/10 bg-[#0A0906] p-5 space-y-3">
                <div>
                  <div className="text-[10px] tracking-[0.22em] uppercase text-[#D8B23E]">Satisfaction</div>
                  <div className="mt-1 text-lg">
                    {audit.form.satisfied
                      ? <span className="text-[#2FB999] font-bold">✅ 100% Satisfied</span>
                      : <span className="text-[#C0392B] font-bold">❌ Not Satisfied</span>}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.22em] uppercase text-[#D8B23E]">Reason for Repeated Trades</div>
                  <div className="mt-1 italic text-[#F5F5F0]">"{audit.form.repeated_trade_reason}"</div>
                </div>
                {audit.form.project_name && (
                  <div>
                    <div className="text-[10px] tracking-[0.22em] uppercase text-[#D8B23E]">Project</div>
                    <div className="mt-1 text-[#F6E4A6]">{audit.form.project_name}</div>
                  </div>
                )}
                <div className="text-[10px] text-[#8A8A82] italic pt-2 border-t border-white/10">
                  Submitted {new Date(audit.submitted_at).toLocaleString()}
                </div>
              </div>
            )}
            {audit.video_url && (
              <div className="rounded-sm border border-[#D8B23E]/40 p-5 bg-[#0A0906]">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] tracking-[0.22em] uppercase text-[#D8B23E]">Permanent Declaration Video</div>
                  <a
                    data-testid="audit-video-download"
                    href={`${process.env.REACT_APP_BACKEND_URL}${audit.video_url}${audit.video_url.includes("?") ? "&" : "?"}download=1`}
                    download
                    className="text-[10px] tracking-[0.22em] uppercase text-[#F6E4A6] hover:text-white border border-[#D8B23E]/60 px-2.5 py-1 rounded-sm">
                    ⬇ Download
                  </a>
                </div>
                <video data-testid="audit-video"
                         src={`${process.env.REACT_APP_BACKEND_URL}${audit.video_url}`}
                         controls preload="metadata"
                         className="mt-3 w-full max-h-[420px] rounded-sm bg-black" />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
